import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PatioModule.css';

interface EntradaVeiculo {
  id: number;
  placa: string;
  nome: string;
  documento: string;
  data_entrada: string;
  motivo: string;
  tipo_entrada: 'Empresa' | 'Entrega' | 'Prestador' | 'Operacao';
  status: 'Entrada' | 'Saida';
  vaga?: string; // Nova propriedade para a vaga
  data_saida?: string;
  // Campos específicos por tipo
  kilometragem?: string; // Empresa
  numero_nota_fiscal?: string; // Entrega
  servico_prestado?: string; // Prestador
  // Operação
  hodometro_km?: string; // Campo livre para Operação
  placa_carreta?: string;
  placa_carreta_saida?: string;
  placa_carreta2?: string;
  verificar_cabo?: boolean;
  verificar_cinta?: boolean;
  observacao?: string;
  tipo_motorista?: string;
  motorista_entrada?: string;
  motorista_saida?: string;
  documento_saida?: string;
  epi_capacete?: boolean;
  epi_colete?: boolean;
  epi_bota?: boolean;
}

interface SugestaoPlaca {
  placa: string;
  marca?: string;
  modelo?: string;
  tipo?: string;
  tipo_veiculo?: string;
}

type StatusValidacaoHodometro = 'idle' | 'loading' | 'ok' | 'error' | 'info';

interface ValidacaoHodometro {
  status: StatusValidacaoHodometro;
  mensagem: string;
  valido: boolean;
}

const TIPOS_ENTRADA: Array<'Empresa' | 'Entrega' | 'Prestador' | 'Operacao'> = ['Empresa', 'Entrega', 'Prestador', 'Operacao'];

const PatioModule: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || '';
  
    // Estado para modal de saída
  const tiposComAutocompletePlaca: Array<'Empresa' | 'Operacao'> = ['Empresa', 'Operacao'];
  const [entradas, setEntradas] = useState<EntradaVeiculo[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<string[]>(['Empresa', 'Entrega', 'Prestador', 'Operacao']);
  const [buscaTexto, setBuscaTexto] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showVagaSelector, setShowVagaSelector] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [vagasOcupadas, setVagasOcupadas] = useState<Set<string>>(new Set());
  const [vagaSelecionada, setVagaSelecionada] = useState<string>('');
  const [sugestoesPlaca, setSugestoesPlaca] = useState<SugestaoPlaca[]>([]);
  const [buscandoPlacas, setBuscandoPlacas] = useState(false);
  const [sugestoesCarreta, setSugestoesCarreta] = useState<SugestaoPlaca[]>([]);
  const [buscandoCarretas, setBuscandoCarretas] = useState(false);
  const [validacaoHodometro, setValidacaoHodometro] = useState<ValidacaoHodometro>({
    status: 'idle',
    mensagem: '',
    valido: true
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportForm, setExportForm] = useState({
    dataInicio: '',
    dataFim: ''
  });
  // Estado para modal de saída
  const [showSaidaModal, setShowSaidaModal] = useState(false);
  const [detalheEntrada, setDetalheEntrada] = useState<EntradaVeiculo | null>(null);
  const [agora, setAgora] = useState(new Date());
  const [saidaData, setSaidaData] = useState<any>(null);
  const [saidaEdit, setSaidaEdit] = useState({
    placa: '',
    placa_carreta: '',
    nome: '',
    documento: ''
  });
  const [form, setForm] = useState({
    placa: '',
    nome: '',
    documento: '',
    data_entrada: (() => { const now = new Date(); const pad = (n: number) => String(n).padStart(2, '0'); return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`; })(),
    motivo: '',
    tipo_entrada: 'Empresa' as 'Empresa' | 'Entrega' | 'Prestador' | 'Operacao',
    // Campos específicos
    kilometragem: '',
    numero_nota_fiscal: '',
    servico_prestado: '',
    hodometro_km: '', // Campo livre para Operação
    placa_carreta: '',
    placa_carreta2: '',
    verificar_cabo: false,
    verificar_cinta: false,
    observacao: '',
    tipo_motorista: '',
    epi_capacete: false,
    epi_colete: false,
    epi_bota: false
  });

  const getLocalDateTimeString = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const normalizarHodometro = (valor: string): number | null => {
    if (!valor) return null;
    const somenteNumeros = valor.trim().replace(/[^0-9.,]/g, '');
    if (!somenteNumeros) return null;

    const numero = Number(somenteNumeros.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(numero) ? numero : null;
  };

  const autocompletePlacaAtivo = tiposComAutocompletePlaca.includes(form.tipo_entrada as 'Empresa' | 'Operacao');

  useEffect(() => {
    fetchEntradas();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setAgora(new Date());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!showModal) {
      setSugestoesPlaca([]);
      setBuscandoPlacas(false);
      setSugestoesCarreta([]);
      setBuscandoCarretas(false);
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;

    if (!autocompletePlacaAtivo) {
      setSugestoesPlaca([]);
      setBuscandoPlacas(false);
      return;
    }

    const termo = form.placa.trim();

    if (termo.length < 2) {
      setSugestoesPlaca([]);
      setBuscandoPlacas(false);
      return;
    }

    let ativo = true;

    const timeoutId = setTimeout(async () => {
      try {
        setBuscandoPlacas(true);
        const response = await axios.get(`${API_URL}/api/patio/placas`, {
          params: {
            termo,
            tipoEntrada: form.tipo_entrada
          }
        });

        if (!ativo) return;

        if ((response.data as any).success) {
          setSugestoesPlaca((response.data as any).data || []);
        } else {
          setSugestoesPlaca([]);
        }
      } catch (error) {
        if (ativo) {
          setSugestoesPlaca([]);
        }
      } finally {
        if (ativo) {
          setBuscandoPlacas(false);
        }
      }
    }, 300);

    return () => {
      ativo = false;
      clearTimeout(timeoutId);
    };
  }, [form.placa, form.tipo_entrada, showModal, autocompletePlacaAtivo]);

  useEffect(() => {
    if (!showModal || form.tipo_entrada !== 'Operacao') {
      setSugestoesCarreta([]);
      setBuscandoCarretas(false);
      return;
    }

    const termo = form.placa_carreta.trim();

    if (termo.length < 2) {
      setSugestoesCarreta([]);
      setBuscandoCarretas(false);
      return;
    }

    let ativo = true;

    const timeoutId = setTimeout(async () => {
      try {
        setBuscandoCarretas(true);
        const response = await axios.get(`${API_URL}/api/patio/placas-carreta`, {
          params: { termo }
        });

        if (!ativo) return;

        if ((response.data as any).success) {
          setSugestoesCarreta((response.data as any).data || []);
        } else {
          setSugestoesCarreta([]);
        }
      } catch (error) {
        if (ativo) {
          setSugestoesCarreta([]);
        }
      } finally {
        if (ativo) {
          setBuscandoCarretas(false);
        }
      }
    }, 300);

    return () => {
      ativo = false;
      clearTimeout(timeoutId);
    };
  }, [form.placa_carreta, form.tipo_entrada, showModal]);

  useEffect(() => {
    if (!showModal || form.tipo_entrada !== 'Operacao') {
      setValidacaoHodometro({ status: 'idle', mensagem: '', valido: true });
      return;
    }

    const placa = form.placa.trim();
    const hodometro = form.hodometro_km.trim();

    if (placa.length < 2 || !hodometro) {
      setValidacaoHodometro({ status: 'idle', mensagem: '', valido: true });
      return;
    }

    if (normalizarHodometro(hodometro) === null) {
      setValidacaoHodometro({
        status: 'error',
        mensagem: 'Hodômetro inválido. Informe apenas números.',
        valido: false
      });
      return;
    }

    let ativo = true;

    const timeoutId = setTimeout(async () => {
      try {
        setValidacaoHodometro({ status: 'loading', mensagem: 'Validando hodômetro...', valido: true });

        const response = await axios.get(`${API_URL}/api/patio/validacao-hodometro`, {
          params: {
            placa,
            hodometro,
            tipoEntrada: form.tipo_entrada
          }
        });

        if (!ativo) return;

        const data = (response.data as any)?.data || {};
        const valido = !!data.valido;
        const mensagem = data.mensagem || '';
        const temHistorico = !!data.temHistorico;

        setValidacaoHodometro({
          status: valido ? (temHistorico ? 'ok' : 'info') : 'error',
          mensagem,
          valido
        });
      } catch (error) {
        if (!ativo) return;
        setValidacaoHodometro({
          status: 'error',
          mensagem: 'Não foi possível validar o hodômetro agora.',
          valido: false
        });
      }
    }, 350);

    return () => {
      ativo = false;
      clearTimeout(timeoutId);
    };
  }, [API_URL, form.hodometro_km, form.placa, form.tipo_entrada, showModal]);

  const fetchEntradas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patio/entradas`);
      if ((response.data as any).success) {
        setEntradas((response.data as any).data);
        
        // Buscar vagas ocupadas
        const vagasResponse = await axios.get(`${API_URL}/api/patio/vagas-ocupadas`);
        if ((vagasResponse.data as any).success) {
          setVagasOcupadas(new Set((vagasResponse.data as any).data));
        }
      }
    } catch (error) {
      console.error('Erro ao buscar entradas:', error);
    }
  };

  const handleFiltroChange = (tipo: string) => {
    if (filtroTipo.includes(tipo)) {
      setFiltroTipo(filtroTipo.filter(t => t !== tipo));
    } else {
      setFiltroTipo([...filtroTipo, tipo]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm({ ...form, [name]: checked });
    } else {
      const camposPlaca = ['placa', 'placa_carreta', 'placa_carreta2'];
      const valorNormalizado = camposPlaca.includes(name)
        ? value.toUpperCase().replace(/\s+/g, '')
        : value;

      setForm({ ...form, [name]: valorNormalizado });

      if (name === 'placa' || name === 'hodometro_km' || name === 'tipo_entrada') {
        setValidacaoHodometro({ status: 'idle', mensagem: '', valido: true });
      }

      if (name === 'placa' && valorNormalizado.length < 2) {
        setSugestoesPlaca([]);
        setBuscandoPlacas(false);
      }

      if (name === 'placa_carreta' && valorNormalizado.length < 2) {
        setSugestoesCarreta([]);
        setBuscandoCarretas(false);
      }

      if (name === 'tipo_entrada' && !tiposComAutocompletePlaca.includes(valorNormalizado as 'Empresa' | 'Operacao')) {
        setSugestoesPlaca([]);
        setBuscandoPlacas(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    if (!form.placa || !form.nome || !form.documento || !form.data_entrada) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (form.tipo_entrada === 'Operacao' && form.hodometro_km) {
      if (normalizarHodometro(form.hodometro_km) === null) {
        alert('Hodômetro inválido. Informe apenas números.');
        return;
      }

      if (validacaoHodometro.status === 'loading') {
        alert('Aguarde a validação do hodômetro.');
        return;
      }

      if (!validacaoHodometro.valido) {
        alert(validacaoHodometro.mensagem || 'Hodômetro deve ser maior que o último registro da placa.');
        return;
      }
    }
    
    // Mostrar seletor de vagas
    setShowVagaSelector(true);
  };

  const confirmarEntrada = async () => {
    if (!vagaSelecionada) {
      alert('Por favor, selecione uma vaga.');
      return;
    }

    if (form.tipo_entrada === 'Operacao' && form.hodometro_km) {
      if (normalizarHodometro(form.hodometro_km) === null) {
        alert('Hodômetro inválido. Informe apenas números.');
        return;
      }

      if (validacaoHodometro.status === 'loading') {
        alert('Aguarde a validação do hodômetro.');
        return;
      }

      if (!validacaoHodometro.valido) {
        alert(validacaoHodometro.mensagem || 'Hodômetro deve ser maior que o último registro da placa.');
        return;
      }
    }

    try {
      const dadosEntrada = {
        ...form,
        vaga: vagaSelecionada,
        data_entrada: new Date(form.data_entrada).toISOString()
      };

      const response = await axios.post(`${API_URL}/api/patio/entradas`, dadosEntrada);
      
      if ((response.data as any).success) {
        // Recarregar a lista de entradas
        await fetchEntradas();
        
        // Reset form
        setForm({
          placa: '',
          nome: '',
          documento: '',
          data_entrada: getLocalDateTimeString(),
          motivo: '',
          tipo_entrada: 'Empresa',
          kilometragem: '',
          numero_nota_fiscal: '',
          servico_prestado: '',
          hodometro_km: '',
          placa_carreta: '',
          placa_carreta2: '',
          verificar_cabo: false,
          verificar_cinta: false,
          observacao: '',
          tipo_motorista: '',
          epi_capacete: false,
          epi_colete: false,
          epi_bota: false
        });
        
        setVagaSelecionada('');
        setSugestoesPlaca([]);
        setValidacaoHodometro({ status: 'idle', mensagem: '', valido: true });
        setShowVagaSelector(false);
        setShowModal(false);
        
        alert('Entrada registrada com sucesso!');
      } else {
        alert((response.data as any).message || 'Erro ao registrar entrada');
      }
    } catch (error: any) {
      console.error('Erro ao registrar entrada:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao registrar entrada';
      alert(errorMessage);
    }
  };

  const handleSaida = async (id: number) => {
    try {
      // Buscar dados da entrada antes de registrar saída
      const entradaResponse = await axios.get(`${API_URL}/api/patio/entradas/${id}`);
      const entradaData = (entradaResponse.data as any).data || {};
      const isCarretaRetida = entradaData.motivo === 'Carreta retida no pátio';
      const placa_carreta_inicial = isCarretaRetida ? entradaData.placa : (entradaData.placa_carreta || '');
      setSaidaData(entradaData);
      setSaidaEdit({
        placa: isCarretaRetida ? '' : (entradaData.placa || ''),
        placa_carreta: placa_carreta_inicial,
        nome: entradaData.motorista_entrada || entradaData.nome || '',
        documento: entradaData.documento || ''
      });
      setShowSaidaModal(true);
    } catch (error: any) {
      alert('Erro ao buscar dados da entrada para saída.');
    }
  };
  // Função para confirmar saída após edição
  const confirmarSaida = async () => {
    if (!saidaData) return;
    try {
      // Aqui, se quiser atualizar campos, pode fazer um PATCH antes
      // Registrar saída
      const response = await axios.patch(`${API_URL}/api/patio/entradas/${saidaData.id}/saida`, {
        placa_saida: saidaEdit.placa,
        placa_carreta_saida: saidaEdit.placa_carreta,
        nome_saida: saidaEdit.nome,
        documento_saida: saidaEdit.documento
      });
      if ((response.data as any).success) {
        await fetchEntradas();
        const carretaFicou = (response.data as any).carretaFicou;
        const placaRetida = (response.data as any).placa_carreta_retida;
        const saidaCombinada = (response.data as any).saidaCombinada;
        if (carretaFicou && placaRetida) {
          alert(`Saída registrada! A carreta ${placaRetida} ficou no pátio e foi registrada na vaga.`);
        } else if (saidaCombinada) {
          alert('Saída registrada com sucesso! Cavalo e carreta foram removidos juntos do pátio.');
        } else {
          alert('Saída registrada com sucesso!');
        }
        setShowSaidaModal(false);
        setSaidaData(null);
      } else {
        alert((response.data as any).message || 'Erro ao registrar saída');
      }
    } catch (error: any) {
      alert('Erro ao registrar saída.');
    }
  };

  const handleExportarRelatorio = async () => {
    if (!exportForm.dataInicio || !exportForm.dataFim) {
      alert('Por favor, preencha ambas as datas');
      return;
    }

    if (new Date(exportForm.dataInicio) > new Date(exportForm.dataFim)) {
      alert('A data de início deve ser anterior à data de fim');
      return;
    }

    setExportLoading(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/patio/relatorio/exportar?dataInicio=${exportForm.dataInicio}&dataFim=${exportForm.dataFim}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao gerar relatório');
      }

      // Criar blob do arquivo Excel
      const blob = await response.blob();
      
      // Criar URL temporária e fazer download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_patio_${exportForm.dataInicio.replace(/-/g, '')}_${exportForm.dataFim.replace(/-/g, '')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Fechar modal e resetar form
      setShowExportModal(false);
      setExportForm({ dataInicio: '', dataFim: '' });
      
      alert('Relatório exportado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao exportar relatório:', error);
      alert(error.message || 'Erro ao exportar relatório');
    } finally {
      setExportLoading(false);
    }
  };

  const isDataHoje = (dataString?: string) => {
    if (!dataString) return false;
    const data = new Date(dataString);
    const hoje = new Date();

    return data.getDate() === hoje.getDate()
      && data.getMonth() === hoje.getMonth()
      && data.getFullYear() === hoje.getFullYear();
  };

  const entradasNoPatio = entradas.filter((entrada) => entrada.status === 'Entrada');
  const buscaNormalizada = buscaTexto.trim().toUpperCase();

  const entradasFiltradas = entradasNoPatio.filter((entrada) => {
    const passouFiltroTipo = filtroTipo.includes(entrada.tipo_entrada);
    if (!passouFiltroTipo) return false;
    if (!buscaNormalizada) return true;

    const camposBusca = [
      entrada.placa,
      entrada.placa_carreta,
      entrada.nome,
      entrada.documento,
      entrada.vaga
    ]
      .filter(Boolean)
      .join(' ')
      .toUpperCase();

    return camposBusca.includes(buscaNormalizada);
  });

  const statDentroPatio = entradasNoPatio.length;
  const statEntradasHoje = entradas.filter((entrada) => entrada.status === 'Entrada' && isDataHoje(entrada.data_entrada)).length;
  const statSaidasHoje = entradas.filter((entrada) => entrada.status === 'Saida' && isDataHoje(entrada.data_saida)).length;
  const statAguardando = entradasNoPatio.filter((entrada) => !entrada.vaga).length;
  const filtrosCompletos = filtroTipo.length === TIPOS_ENTRADA.length;

  const formatarData = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR') + ', ' + data.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatarTempoDecorrido = (dataString: string) => {
    const entrada = new Date(dataString);
    const diffMs = agora.getTime() - entrada.getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return 'Agora';

    const diffTotalMinutos = Math.floor(diffMs / 60000);
    const horas = Math.floor(diffTotalMinutos / 60);
    const minutos = diffTotalMinutos % 60;

    if (horas > 0) {
      return `Há ${String(horas).padStart(2, '0')}h ${String(minutos).padStart(2, '0')}m`;
    }

    return `Há ${String(minutos).padStart(2, '0')}m`;
  };

  const formatarPlacaMercosul = (placa?: string | null) => {
    const limpa = String(placa || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

    if (!limpa) return '-';
    if (limpa.length <= 7) return limpa;
    return limpa.slice(0, 7);
  };

  const getTipoClass = (tipo: string) => {
    const classes = {
      'Empresa': 'tipo-empresa',
      'Entrega': 'tipo-entrega', 
      'Prestador': 'tipo-prestador',
      'Operacao': 'tipo-operacao',
      'Carreta': 'tipo-carreta'
    };
    return classes[tipo as keyof typeof classes] || '';
  };

  const getStatusTag = (entrada: EntradaVeiculo): { label: string; className: string } => {
    const motivo = (entrada.motivo || '').toLowerCase();

    if (!entrada.vaga) return { label: 'Aguardando vaga', className: 'status-aguardando' };
    if (motivo.includes('atras')) return { label: 'Atrasado', className: 'status-atrasado' };
    if (motivo.includes('libera')) return { label: 'Aguardando liberação', className: 'status-liberacao' };
    if (motivo.includes('descarg')) return { label: 'Aguardando descarga', className: 'status-aguardando' };
    if (motivo.includes('document')) return { label: 'Aguardando documentação', className: 'status-documentacao' };
    if (motivo.includes('liberad')) return { label: 'Liberado', className: 'status-liberado' };

    return { label: 'Dentro do pátio', className: 'status-dentro' };
  };

  const renderMapaVagas = () => {
    const vagasEscritorios = ['E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7'];
    const vagasPortaria = ['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12', 'A13'];
    const vagasB = ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8'];
    const vagasC = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8'];
    const vagasD = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8'];

    const todasVagas = [...vagasEscritorios, ...vagasPortaria, ...vagasB, ...vagasC, ...vagasD];
    const vagasLivres = todasVagas.filter((vaga) => !vagasOcupadas.has(vaga)).length;
    const vagasOcupadasQtd = todasVagas.length - vagasLivres;
    const vagasSelecionadasQtd = vagaSelecionada ? 1 : 0;

    const renderVaga = (vaga: string) => {
      const ocupada = vagasOcupadas.has(vaga);
      const selecionada = vagaSelecionada === vaga;

      return (
        <button
          key={vaga}
          type="button"
          disabled={ocupada}
          className={`vaga-ui-slot ${ocupada ? 'is-occupied' : 'is-free'} ${selecionada ? 'is-selected' : ''}`}
          onClick={() => !ocupada && setVagaSelecionada(vaga)}
          aria-label={`Selecionar vaga ${vaga}`}
        >
          {vaga}
        </button>
      );
    };

    return (
      <div className="vaga-ui">
        <div className="vaga-ui-header">
          <div className="vaga-ui-header-left">
            <div className="vaga-ui-header-icon">P</div>
            <div>
              <h3>Selecionar Vaga</h3>
              <p>Escolha a vaga onde o veículo será estacionado.</p>
            </div>
          </div>
          <button className="vaga-ui-close" onClick={() => setShowVagaSelector(false)} aria-label="Fechar modal de vagas">×</button>
        </div>

        <div className="vaga-ui-body">
          <div className="vaga-ui-topbar">
            <div className="vaga-ui-summary-grid">
              <div className="vaga-ui-summary-card is-free">
                <span className="vaga-ui-summary-icon">✓</span>
                <div>
                  <small>Livres</small>
                  <strong>{vagasLivres}</strong>
                </div>
              </div>
              <div className="vaga-ui-summary-card is-occupied">
                <span className="vaga-ui-summary-icon">×</span>
                <div>
                  <small>Ocupadas</small>
                  <strong>{vagasOcupadasQtd}</strong>
                </div>
              </div>
              <div className="vaga-ui-summary-card is-selected">
                <span className="vaga-ui-summary-icon">○</span>
                <div>
                  <small>Selecionada</small>
                  <strong>{vagasSelecionadasQtd}</strong>
                </div>
              </div>
            </div>

            <div className="vaga-ui-legend-row">
              <span className="vaga-ui-legend-item"><i className="vaga-ui-dot is-free"></i>Livre</span>
              <span className="vaga-ui-legend-item"><i className="vaga-ui-dot is-occupied"></i>Ocupada</span>
              <span className="vaga-ui-legend-item"><i className="vaga-ui-dot is-selected"></i>Selecionada</span>
              <span className="vaga-ui-legend-item"><i className="vaga-ui-dot is-myspot"></i>Sua Vaga</span>
              <button type="button" className="vaga-ui-help-btn">i Entenda o mapa</button>
            </div>
          </div>

          <div className="vaga-ui-map-grid">
            <section className="vaga-ui-section">
              <h4>ESCRITÓRIOS</h4>
              <div className="vaga-ui-escritorios-grid">
                {vagasEscritorios.map(renderVaga)}
              </div>
            </section>

            <section className="vaga-ui-section vaga-ui-patio-section">
              <h4>PÁTIO</h4>
              <div className="vaga-ui-patio-columns">
                <div className="vaga-ui-coluna">
                  <span>COLUNA B</span>
                  {vagasB.map(renderVaga)}
                </div>
                <div className="vaga-ui-coluna">
                  <span>COLUNA C</span>
                  {vagasC.map(renderVaga)}
                </div>
                <div className="vaga-ui-coluna">
                  <span>COLUNA D</span>
                  {vagasD.map(renderVaga)}
                </div>
              </div>
            </section>

            <section className="vaga-ui-section">
              <h4>PORTARIA</h4>
              <div className="vaga-ui-portaria-grid">
                {vagasPortaria.map(renderVaga)}
              </div>
            </section>
          </div>

          <div className="vaga-ui-footer">
            <div className="vaga-ui-selected-pill">
              <span>Vaga selecionada</span>
              <strong>{vagaSelecionada || '-'}</strong>
            </div>

            <div className="vaga-ui-actions">
              <button type="button" onClick={() => setShowVagaSelector(false)} className="vaga-ui-btn-cancelar">
                Cancelar
              </button>
              <button type="button" onClick={confirmarEntrada} className="vaga-ui-btn-confirmar" disabled={!vagaSelecionada}>
                Confirmar vaga {vagaSelecionada || ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCamposEspecificos = () => {
    switch (form.tipo_entrada) {
      case 'Empresa':
        return (
          <div className="form-group">
            <label>Kilometragem</label>
            <input 
              type="text" 
              name="kilometragem" 
              value={form.kilometragem}
              onChange={handleInputChange}
              placeholder="Kilometragem do veículo"
            />
          </div>
        );

      case 'Entrega':
        return (
          <div className="form-group">
            <label>Número da nota fiscal</label>
            <input 
              type="text" 
              name="numero_nota_fiscal" 
              value={form.numero_nota_fiscal}
              onChange={handleInputChange}
              placeholder="Número da nota fiscal"
            />
          </div>
        );

      case 'Prestador':
        return (
          <div className="form-group">
            <label>Serviço a ser prestado</label>
            <input 
              type="text" 
              name="servico_prestado" 
              value={form.servico_prestado}
              onChange={handleInputChange}
              placeholder="Descrição do serviço"
            />
          </div>
        );

      case 'Operacao':
        return (
          <>
            <div className="form-group">
              <label>Observação</label>
              <textarea 
                name="observacao" 
                value={form.observacao}
                onChange={handleInputChange}
                placeholder="Observações gerais"
                rows={3}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const isSaidaCarretaRetida = showSaidaModal && !!saidaData && saidaData.motivo === 'Carreta retida no pátio';

  return (
    <div className="patio-module">
      <div className="patio-header">
        <div>
          <h2>Controle de Pátio</h2>
          <p className="patio-subtitle">Entradas e saídas de veículos</p>
        </div>
        <div className="header-actions">
          <span className="patio-clock">🕘 {agora.toLocaleDateString('pt-BR')} {agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
          <button className="btn-exportar" onClick={() => setShowExportModal(true)}>
            Exportar
          </button>
          <button className="btn-nova-entrada" onClick={() => setShowModal(true)}>
            + Nova entrada
          </button>
        </div>
      </div>

      <div className="patio-stats-grid">
        <div className="patio-stat-card stat-green">
          <div className="patio-stat-icon">🚛</div>
          <div>
            <p>Dentro do pátio</p>
            <strong>{statDentroPatio}</strong>
            <span>veículos</span>
          </div>
        </div>
        <div className="patio-stat-card stat-blue">
          <div className="patio-stat-icon">⬅️</div>
          <div>
            <p>Entradas hoje</p>
            <strong>{statEntradasHoje}</strong>
            <span>veículos</span>
          </div>
        </div>
        <div className="patio-stat-card stat-red">
          <div className="patio-stat-icon">➡️</div>
          <div>
            <p>Saídas hoje</p>
            <strong>{statSaidasHoje}</strong>
            <span>veículos</span>
          </div>
        </div>
        <div className="patio-stat-card stat-yellow">
          <div className="patio-stat-icon">⏱️</div>
          <div>
            <p>Aguardando</p>
            <strong>{statAguardando}</strong>
            <span>veículos</span>
          </div>
        </div>
      </div>

      <div className="patio-toolbar">
        <div className="patio-filter-buttons">
          <button
            type="button"
            className={`filter-pill ${filtrosCompletos ? 'active' : ''}`}
            onClick={() => setFiltroTipo([...TIPOS_ENTRADA])}
          >
            Todos
          </button>
          {TIPOS_ENTRADA.map((tipo) => (
            <button
              key={tipo}
              type="button"
              className={`filter-pill ${getTipoClass(tipo)} ${filtroTipo.includes(tipo) ? 'active' : ''}`}
              onClick={() => handleFiltroChange(tipo)}
            >
              {tipo === 'Operacao' ? 'Operação' : tipo}
            </button>
          ))}
        </div>

        <div className="patio-search-box">
          <span>🔎</span>
          <input
            type="text"
            value={buscaTexto}
            onChange={(e) => setBuscaTexto(e.target.value)}
            placeholder="Buscar por placa, nome, vaga..."
          />
        </div>
      </div>

      <div className="patio-lista">
        {entradasFiltradas.map((entrada) => {
          const isCarretaRetida = entrada.motivo === 'Carreta retida no pátio';
          const tipoDisplay = isCarretaRetida ? 'Carreta' : entrada.tipo_entrada;
          const statusTag = getStatusTag(entrada);

          return (
          <article key={entrada.id} className={`patio-list-item ${getTipoClass(tipoDisplay)}`}>
            <div className="patio-list-main">
              <div className="patio-veiculo-col">
                <div className="plate-badge" aria-label={`Placa ${formatarPlacaMercosul(entrada.placa)}`}>
                  <span className="plate-badge-top">BRASIL</span>
                  <strong className="plate-badge-code">{formatarPlacaMercosul(entrada.placa)}</strong>
                </div>
                <div className="patio-motorista-col">
                  <p className="patio-motorista-nome">{entrada.nome || 'Sem identificação'}</p>
                  {!isCarretaRetida && entrada.placa_carreta && (
                    <small className="patio-carreta-texto">Carreta: {formatarPlacaMercosul(entrada.placa_carreta)}</small>
                  )}
                </div>
              </div>

              <div className="patio-info-col">
                <span className="patio-col-label">Entrada</span>
                <strong>{formatarData(entrada.data_entrada)}</strong>
                <small>{formatarTempoDecorrido(entrada.data_entrada)}</small>
              </div>

              <div className="patio-info-col">
                <span className="patio-col-label">Vaga</span>
                <strong>{entrada.vaga || '-'}</strong>
              </div>

              <div className="patio-info-col patio-status-col">
                <span className="patio-col-label">Status</span>
                <span className={`patio-status-chip ${statusTag.className}`}>{statusTag.label}</span>
              </div>
            </div>

            <div className="patio-list-actions">
              <button
                className="btn-ver-detalhes"
                onClick={() => setDetalheEntrada(entrada)}
              >
                Ver detalhes
              </button>
              <button 
                className="btn-saida"
                onClick={() => handleSaida(entrada.id)}
              >
                Registrar saída
              </button>
            </div>
          </article>
          );
        })}
      </div>

      {entradasFiltradas.length === 0 && (
        <div className="empty-state">
          <p>Nenhum veículo encontrado com os filtros atuais.</p>
        </div>
      )}

      {detalheEntrada && (
        <div className="modal-overlay" onClick={() => setDetalheEntrada(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalhes do veículo</h3>
              <button className="btn-close" onClick={() => setDetalheEntrada(null)}>×</button>
            </div>
            <div className="modal-body patio-detalhes-grid">
              <div className="patio-detalhe-item"><strong>Placa:</strong> <span className="plate-inline">{formatarPlacaMercosul(detalheEntrada.placa)}</span></div>
              <div className="patio-detalhe-item"><strong>Carreta:</strong> <span className="plate-inline">{formatarPlacaMercosul(detalheEntrada.placa_carreta)}</span></div>
              <div className="patio-detalhe-item"><strong>Nome:</strong> {detalheEntrada.nome || '-'}</div>
              <div className="patio-detalhe-item"><strong>Documento:</strong> {detalheEntrada.documento || '-'}</div>
              <div className="patio-detalhe-item"><strong>Tipo:</strong> {detalheEntrada.tipo_entrada}</div>
              <div className="patio-detalhe-item"><strong>Entrada:</strong> {formatarData(detalheEntrada.data_entrada)}</div>
              <div className="patio-detalhe-item"><strong>Vaga:</strong> {detalheEntrada.vaga || '-'}</div>
              <div className="patio-detalhe-item"><strong>Motivo:</strong> {detalheEntrada.motivo || '-'}</div>
              {detalheEntrada.hodometro_km && (
                <div className="patio-detalhe-item"><strong>Hodômetro:</strong> {detalheEntrada.hodometro_km} km</div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setDetalheEntrada(null)} className="btn-cancelar">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Nova Entrada */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Checklist de Entrada</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="entrada-form">
              <div className="form-group">
                <label>Tipo de entrada</label>
                <select 
                  name="tipo_entrada" 
                  value={form.tipo_entrada} 
                  onChange={handleInputChange}
                  required
                >
                  <option value="Empresa">Empresa</option>
                  <option value="Entrega">Entrega</option>
                  <option value="Prestador">Prestador</option>
                  <option value="Operacao">Operação</option>
                </select>
              </div>

              <div className="form-group">
                <label>Data e hora da entrada *</label>
                <input 
                  type="datetime-local" 
                  name="data_entrada" 
                  value={form.data_entrada}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Placa *</label>
                <input 
                  type="text" 
                  name="placa" 
                  value={form.placa}
                  onChange={handleInputChange}
                  list={autocompletePlacaAtivo ? 'placas-sugeridas-patio' : undefined}
                  autoComplete="off"
                  placeholder="Digite a placa do veículo"
                  required
                />
                {autocompletePlacaAtivo && (
                  <>
                    <datalist id="placas-sugeridas-patio">
                      {sugestoesPlaca.map((item) => (
                        <option
                          key={item.placa}
                          value={item.placa}
                          label={[item.marca, item.modelo, item.tipo].filter(Boolean).join(' • ')}
                        />
                      ))}
                    </datalist>
                    {buscandoPlacas && <small>Buscando placas...</small>}
                  </>
                )}
              </div>

              {form.tipo_entrada === 'Operacao' && (
                <div className="form-group">
                  <label>Hodômetro (KM)</label>
                  <input
                    type="text"
                    name="hodometro_km"
                    value={form.hodometro_km}
                    onChange={handleInputChange}
                    className={
                      validacaoHodometro.status === 'error'
                        ? 'campo-invalido'
                        : validacaoHodometro.status === 'ok'
                          ? 'campo-valido'
                          : ''
                    }
                    placeholder="Informe o hodômetro (KM)"
                  />
                  {validacaoHodometro.status !== 'idle' && (
                    <small className={`hodometro-feedback hodometro-feedback--${validacaoHodometro.status}`}>
                      {validacaoHodometro.mensagem}
                    </small>
                  )}
                </div>
              )}

              {form.tipo_entrada === 'Operacao' && (
                <div className="form-group">
                  <label>Placa Carreta</label>
                  <input 
                    type="text" 
                    name="placa_carreta" 
                    value={form.placa_carreta}
                    onChange={handleInputChange}
                    list="placas-carreta-sugeridas-patio"
                    autoComplete="off"
                    placeholder="Placa da carreta"
                  />
                  <datalist id="placas-carreta-sugeridas-patio">
                    {sugestoesCarreta.map((item) => (
                      <option
                        key={item.placa}
                        value={item.placa}
                        label={[item.marca, item.modelo, item.tipo].filter(Boolean).join(' • ')}
                      />
                    ))}
                  </datalist>
                  {buscandoCarretas && <small>Buscando carretas...</small>}
                </div>
              )}

              {form.tipo_entrada === 'Operacao' && (
                <div className="form-group">
                  <label>Validação Carreta</label>
                  <div className="checkbox-row checkbox-row--chips">
                    <label className="checkbox-label checkbox-label--chip">
                      <input
                        type="checkbox"
                        name="verificar_cabo"
                        checked={form.verificar_cabo}
                        onChange={handleInputChange}
                      />
                      <span>Verificar Cabo</span>
                    </label>
                    <label className="checkbox-label checkbox-label--chip">
                      <input
                        type="checkbox"
                        name="verificar_cinta"
                        checked={form.verificar_cinta}
                        onChange={handleInputChange}
                      />
                      <span>Verificar Cinta</span>
                    </label>
                  </div>
                </div>
              )}

              {form.tipo_entrada === 'Operacao' && (
                <div className="form-group">
                  <label>Tipo de motorista</label>
                  <select 
                    name="tipo_motorista" 
                    value={form.tipo_motorista}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="Casa">Casa</option>
                    <option value="Terceiro">Terceiro</option>
                    <option value="Freelancer">Freelancer</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Nome completo *</label>
                <input 
                  type="text" 
                  name="nome" 
                  value={form.nome}
                  onChange={handleInputChange}
                  placeholder="Nome do motorista"
                  required
                />
              </div>

              <div className="form-group">
                <label>Documento *</label>
                <input 
                  type="text" 
                  name="documento" 
                  value={form.documento}
                  onChange={handleInputChange}
                  placeholder="CPF ou documento"
                  required
                />
              </div>

              {form.tipo_entrada === 'Operacao' && (
                <div className="form-group">
                  <label>EPIs</label>
                  <div className="checkbox-row checkbox-row--chips">
                    <label className="checkbox-label checkbox-label--chip">
                      <input
                        type="checkbox"
                        name="epi_capacete"
                        checked={form.epi_capacete}
                        onChange={handleInputChange}
                      />
                      <span>Capacete</span>
                    </label>
                    <label className="checkbox-label checkbox-label--chip">
                      <input
                        type="checkbox"
                        name="epi_colete"
                        checked={form.epi_colete}
                        onChange={handleInputChange}
                      />
                      <span>Colete</span>
                    </label>
                    <label className="checkbox-label checkbox-label--chip">
                      <input
                        type="checkbox"
                        name="epi_bota"
                        checked={form.epi_bota}
                        onChange={handleInputChange}
                      />
                      <span>Bota</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Campos específicos por tipo */}
              {renderCamposEspecificos()}

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn-registrar">
                  REGISTRAR
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Vagas */}
      {showVagaSelector && (
        <div className="modal-overlay" onClick={() => setShowVagaSelector(false)}>
          <div className="modal-content-vaga" onClick={(e) => e.stopPropagation()}>
            {renderMapaVagas()}
          </div>
        </div>
      )}

      {/* Modal de Exportação de Relatório */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Exportar Relatório para Excel</h3>
              <button className="btn-close" onClick={() => setShowExportModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="export-form">
                <div className="form-group">
                  <label>Data de Início:</label>
                  <input
                    type="date"
                    value={exportForm.dataInicio}
                    onChange={(e) => setExportForm({...exportForm, dataInicio: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Data de Fim:</label>
                  <input
                    type="date"
                    value={exportForm.dataFim}
                    onChange={(e) => setExportForm({...exportForm, dataFim: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                onClick={() => setShowExportModal(false)} 
                className="btn-cancelar"
                disabled={exportLoading}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                onClick={handleExportarRelatorio} 
                className="btn-confirmar"
                disabled={exportLoading}
              >
                {exportLoading ? 'Gerando...' : '📊 Exportar Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Saída */}
      {showSaidaModal && saidaData && (
        <div className="modal-overlay" onClick={() => setShowSaidaModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registrar Saída</h3>
              <button className="btn-close" onClick={() => setShowSaidaModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Placa</label>
                <input
                  type="text"
                  value={isSaidaCarretaRetida ? saidaEdit.placa : saidaData.placa}
                  onChange={e => setSaidaEdit({...saidaEdit, placa: e.target.value.toUpperCase().replace(/\s+/g, '')})}
                  disabled={!isSaidaCarretaRetida}
                  required={isSaidaCarretaRetida}
                />
              </div>
              <div className="form-group">
                <label>Placa Carreta {isSaidaCarretaRetida && <span style={{color: 'red'}}>*</span>}</label>
                <input 
                  type="text" 
                  value={saidaEdit.placa_carreta} 
                  onChange={e => setSaidaEdit({...saidaEdit, placa_carreta: e.target.value})} 
                  required={isSaidaCarretaRetida}
                  disabled={isSaidaCarretaRetida}
                />
              </div>
              <div className="form-group">
                <label>Motorista</label>
                <input type="text" value={saidaEdit.nome} onChange={e => setSaidaEdit({...saidaEdit, nome: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Documento</label>
                <input type="text" value={saidaEdit.documento} onChange={e => setSaidaEdit({...saidaEdit, documento: e.target.value})} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowSaidaModal(false)} className="btn-cancelar">Cancelar</button>
              <button 
                type="button" 
                onClick={confirmarSaida} 
                className="btn-registrar"
                disabled={isSaidaCarretaRetida && (!saidaEdit.placa_carreta.trim() || !saidaEdit.placa.trim())}
              >
                Confirmar Saída
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatioModule;